import {
  Truck,
  FileText,
  LayoutDashboard,
  MessageSquare,
  Settings,
  type LucideIcon,
  Store,
} from 'lucide-react';
import { OrgAudience, type AllowedOrgType } from '../guards/permissions';
import { ROUTE_PERMISSIONS } from '../guards/route-permissions';

/** Which field of GET /nav/counts drives this item's badge. Omit = no badge. */
export type NavBadgeKey = 'rfqs' | 'orders';

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  badgeKey?: NavBadgeKey;
  allowedOrgTypes?: readonly AllowedOrgType[];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { label: 'Overview', path: '/', icon: LayoutDashboard },
      { label: 'Storefront', path: '/storefront', icon: Store },
      { label: 'Requests for quote', path: '/rfqs', icon: FileText, badgeKey: 'rfqs' },
      { label: 'Orders', path: '/orders', icon: Truck, badgeKey: 'orders' },
      { label: 'Messages', path: '/messages', icon: MessageSquare },
    ],
  },
];

export const SETTINGS_ITEM = { label: 'Settings', path: '/settings', icon: Settings };

/**
 * Resolve the breadcrumb label for a URL by matching the nav item whose path is its longest prefix.
 * @param pathname Current location pathname.
 * @returns The matching nav item's label, or undefined when the route sits under no nav entry.
 */
export function navLabelForPath(pathname: string): string | undefined {
  const candidates = [...NAV_GROUPS.flatMap((group) => group.items), SETTINGS_ITEM];

  const match = candidates
    .filter((item) =>
      item.path === '/'
        ? pathname === '/'
        : pathname === item.path || pathname.startsWith(item.path + '/'),
    )
    .sort((a, b) => b.path.length - a.path.length)[0];

  return match?.label;
}

/** Filter nav groups by the active organization's type ('purchaser' | 'supplier'), dropping empty groups. */
export function navGroupsForOrgType(type: string | undefined | null): NavGroup[] {
  const t = (type ?? '').toLowerCase();
  if (t !== OrgAudience.PURCHASER && t !== OrgAudience.SUPPLIER) return NAV_GROUPS; // unknown → full union

  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      const audience = item.allowedOrgTypes ?? ROUTE_PERMISSIONS[item.path]?.allowedOrgTypes;
      if (!audience) return true; // unrestricted route

      return audience.includes(t as AllowedOrgType);
    }),
  })).filter((group) => group.items.length > 0);
}
